'use client';

import { useEffect, useRef } from 'react';

interface UseWheelPageNavigationProps {
  onPrev: () => void;
  onNext: () => void;
  cooldownMs: number;
  enabled?: boolean;
}

const WHEEL_MIN_DELTA = 10;

// Module-level so it survives the page unmounting and remounting on navigation.
// Stores the absolute timestamp (ms) at which the next scroll-driven
// navigation is allowed to fire.
let wheelPageNavigationCooldownUntil = 0;

export function useWheelPageNavigation({ onPrev, onNext, cooldownMs, enabled = true }: UseWheelPageNavigationProps) {
  const callbacksRef = useRef({ onPrev, onNext });

  useEffect(() => {
    callbacksRef.current = { onPrev, onNext };
  }, [onPrev, onNext]);

  useEffect(() => {
    if (!enabled) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      if (Math.abs(event.deltaY) < WHEEL_MIN_DELTA) return;

      const now = Date.now();
      if (now < wheelPageNavigationCooldownUntil) return;
      wheelPageNavigationCooldownUntil = now + cooldownMs;

      if (event.deltaY < 0) {
        callbacksRef.current.onPrev();
      } else {
        callbacksRef.current.onNext();
      }
    };

    window.addEventListener('wheel', onWheel);
    return () => {
      window.removeEventListener('wheel', onWheel);
    };
  }, [enabled, cooldownMs]);
}
